import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Table, TableColumnType, Space, Tooltip, notification } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faPlus } from '@fortawesome/pro-solid-svg-icons'
import InvoiceByJobFilters from '../InvoiceByJob/InvoiceByJobFilters'
import EditorDrawer from './EditorDrawer/EditorDrawer'
import ReadyToInvoiceContext from './ReadyToInvoiceContext'
import { useInvoiceByJobFilters } from '../InvoiceByJob/useInvoiceByJobFilters'
import { useActiveBatchQuery, useBatchCreateInvoicesMutation, useReadyToInvoiceJobsQuery } from '../../../domain/invoicing/invoicingQueries'
import { useSynkedUPPaymentsStatus } from '../../../domain/invoicing/settings/invoicingSettingsQueries'
import { unknownTooltip } from '../synkedUPPaymentsStatusModel'
import { BatchCreateRequest, BatchCreateResponse } from '../../../domain/invoicing/invoicingApi'
import { JobForInvoicing } from '../../../domain/invoicing/invoice'
import { READY_TO_INVOICE_STORAGE_KEYS } from '../InvoiceByJob/filtersModel'
import { INTERVAL_ONLY_BATCH_TOOLTIP, isBatchProcessing, isIntervalOnlyJob } from '../BatchCreate/batchInvoicingModel'
import { batchScope } from '../BatchCreate/batchScopeModel'
import CreateInvoicesModal from '../BatchCreate/CreateInvoicesModal'
import BatchInvoicingUpgradeModal from '../BatchInvoicingUpgradeModal'
import VSpace from '../../common/VSpace'
import JobTypeTag from '../JobTypeTag'
import InvoiceableWorkareaPills from './InvoiceableWorkareaPills'
import { readyToInvoiceRequest, DATE_HELP } from './readyToInvoiceModel'

type Props = {
  googleApiKey: string
  onBatchSuccess: (response: BatchCreateResponse, request: BatchCreateRequest) => void
}

const ReadyToInvoiceView = ({ googleApiKey, onBatchSuccess }: Props) => {
  const filters = useInvoiceByJobFilters({ storageKeys: READY_TO_INVOICE_STORAGE_KEYS, searchDebounceMs: 300 })
  const request = useMemo(() => readyToInvoiceRequest(filters.appliedState), [filters.appliedState])
  const [selectedJobId, setSelectedJobId] = useState<number>()
  const [hasPendingEdits, setHasPendingEdits] = useState(false)
  const selectJob = useCallback((jobId?: number) => {
    if (jobId === selectedJobId) {
      return
    }
    if (selectedJobId !== undefined && hasPendingEdits) {
      notification.info({ message: 'Please wait for changes to finish saving before leaving this job.' })
      return
    }
    setHasPendingEdits(false)
    setSelectedJobId(jobId)
  }, [selectedJobId, hasPendingEdits])
  const clearSelection = useCallback(() => selectJob(undefined), [selectJob])
  const [selectedJobIds, setSelectedJobIds] = useState<number[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const activeBatchQuery = useActiveBatchQuery()
  const batchStatus = activeBatchQuery.data?.status
  const isBatchRunning = isBatchProcessing(batchStatus)

  const jobsQuery = useReadyToInvoiceJobsQuery(request)
  const jobs = useMemo(() => jobsQuery.data?.jobs ?? [], [jobsQuery.data])
  const defaultDueInDays = jobsQuery.data?.defaultDueInDays ?? 0
  const synkedUPPaymentsStatus = useSynkedUPPaymentsStatus()
  const { isTenantOnSynkedUPPayments, isKnown: isSynkedUPPaymentsStatusKnown } = synkedUPPaymentsStatus
  const settingsTooltip = unknownTooltip(synkedUPPaymentsStatus)

  const scope = useMemo(() => batchScope(request), [request])

  const mutation = useBatchCreateInvoicesMutation((response, batchRequest) => {
    setShowCreateModal(false)
    setSelectedJobIds([])
    onBatchSuccess(response, batchRequest)
  })

  const columns: TableColumnType<JobForInvoicing>[] = [
    { title: 'Customer', dataIndex: ['customer', 'displayName'] },
    {
      title: 'Job',
      key: 'job',
      render: (_: unknown, job: JobForInvoicing) => <Space wrap>
        <span>{job.name}</span><JobTypeTag type={job.type} />
      </Space>
    },
    {
      title: 'Invoiceable Work Areas',
      key: 'invoiceableWorkAreas',
      render: (_: unknown, job: JobForInvoicing) => <InvoiceableWorkareaPills workareas={job.invoiceableWorkAreas ?? []} />
    },
    { title: 'Visits', dataIndex: 'visitsCount' },
    { title: 'Last Visit', dataIndex: 'lastVisitDate' },
    {
      width: 40,
      render: () => <FontAwesomeIcon icon={faChevronRight} />
    }
  ]

  useEffect(() => { setSelectedJobIds([]) }, [request])

  const hasSelection = selectedJobIds.length > 0
  const batchDisabled = isBatchRunning || !hasSelection || !isSynkedUPPaymentsStatusKnown
  const batchTooltip = isBatchRunning
    ? 'Invoice generation is currently in progress'
    : !hasSelection
      ? 'Select at least one job to invoice'
      : settingsTooltip

  const handleBatchClick = () => {
    if (!isSynkedUPPaymentsStatusKnown) {
      return
    }
    if (isTenantOnSynkedUPPayments) {
      setShowCreateModal(true)
    } else {
      setShowUpgradeModal(true)
    }
  }

  const batchButton = (
    <Tooltip title={batchTooltip}>
      <Button
        type="primary"
        icon={<FontAwesomeIcon icon={faPlus} />}
        onClick={handleBatchClick}
        disabled={batchDisabled}
      >
        Create Invoices
      </Button>
    </Tooltip>
  )

  const refreshJobs = useCallback(() => { jobsQuery.refetch() }, [jobsQuery.refetch])
  const context = useMemo(() => ({
    currentSelection: selectedJobId === undefined ? undefined : { jobId: selectedJobId },
    selectJob,
    clearSelection,
    jobs,
    isLoadingJobs: jobsQuery.isLoading,
    hasJobsError: jobsQuery.isError,
    refreshJobs,
    setHasPendingEdits,
    filters,
    googleApiKey,
  }), [selectedJobId, selectJob, clearSelection, jobs, jobsQuery.isLoading, jobsQuery.isError, refreshJobs, filters, googleApiKey])

  return <ReadyToInvoiceContext.Provider value={context}><VSpace>
    <div style={{ backgroundColor: 'white', padding: '8px' }}>
      <InvoiceByJobFilters
        filters={filters}
        endAddition={batchButton}
        hideVisitStatus
        showVisitReviewStatus
        dateHelp={DATE_HELP}
      />
    </div>

    <Card>
      <Table<JobForInvoicing>
        size="small"
        columns={columns}
        dataSource={jobs}
        rowKey="id"
        loading={jobsQuery.isLoading || jobsQuery.isFetching}
        pagination={{ pageSize: 100 }}
        rowSelection={{
          selectedRowKeys: selectedJobIds,
          onChange: (keys) => setSelectedJobIds(keys as number[]),
          getCheckboxProps: (job) => ({ disabled: isIntervalOnlyJob(job) }),
          renderCell: (_value, job, _index, originNode) =>
            isIntervalOnlyJob(job)
              ? <Tooltip title={INTERVAL_ONLY_BATCH_TOOLTIP}><span>{originNode}</span></Tooltip>
              : originNode
        }}
        onRow={(record) => ({
          onClick: (e) => {
            const target = e.target as HTMLElement
            if (target.closest('.ant-checkbox-wrapper')) {
              return
            }
            selectJob(record.id)
          },
          style: { cursor: 'pointer' }
        })}
      />
    </Card>

    <EditorDrawer />

    <CreateInvoicesModal
      open={showCreateModal}
      onClose={() => setShowCreateModal(false)}
      jobCount={selectedJobIds.length}
      scope={scope}
      defaultDueInDays={defaultDueInDays}
      confirmLoading={mutation.isLoading}
      onConfirm={(dueDate) => mutation.mutate({
        ...scope,
        jobIds: selectedJobIds,
        dueDate
      })}
    />

    <BatchInvoicingUpgradeModal
      open={showUpgradeModal}
      onClose={() => setShowUpgradeModal(false)}
    />
  </VSpace></ReadyToInvoiceContext.Provider>
}

export default ReadyToInvoiceView
